import Button from '@mui/material/Button';
import { setChartTemplateAdditionnalQueryParams } from 'modules/decision/helpers/chartTemplates';
import { queryToSearch } from 'modules/decision/pages/Explore/components/Sentence/helpers';
import { createEmptyFilterTree } from 'modules/decision/pages/Explore/helpers/filterTree';
import type { LibraryIndicatorT } from 'modules/decision/types/indicators';
import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import AddToDashboardModal from 'shared/components/ChartEditMenu/AddToDashboardModal';
import Flex from 'ui/Flex/Flex';

import { isCityIndicator } from '../helpers';
import SeeOriginalButton from './SeeOriginalButton';

type IndicatorsActionsProps = {
    indicator: LibraryIndicatorT;
    isExploreUser: boolean;
    isInExplore?: boolean;
    showCopyButton?: boolean;
    onUseAsModel?: () => void;
};

const IndicatorActions = ({
    indicator,
    isExploreUser,
    isInExplore = false,
    showCopyButton = true,
    onUseAsModel,
}: IndicatorsActionsProps) => {
    const { t } = useTranslation();
    const isModel = !isCityIndicator(indicator);

    const { dimensions, filterTree, key, metrics, settings, title, vizType } =
        indicator;

    const [showAddToDashboardModal, setShowAddToDashboardModal] =
        useState(false);

    const modelQuery: ExploreQueryT = {
        metrics,
        dimensions,
        filters: filterTree ?? createEmptyFilterTree(),
    };

    const useModelUrl = `/explore?${setChartTemplateAdditionnalQueryParams(
        queryToSearch(modelQuery),
        key,
        isModel,
    )}`;

    const pendoClassNamePrefix = isInExplore
        ? `pendo-indicators-explore${isModel ? '-template' : ''}`
        : `pendo-indicators-library${isModel ? '-template' : ''}`;
    return (
        <>
            <Flex gap={2}>
                {isInExplore && isCityIndicator(indicator) && (
                    <SeeOriginalButton
                        isInExplore
                        route={
                            isExploreUser
                                ? `/explore/${indicator.key}`
                                : `/dashboard/${indicator?.dashboardSlug}#widget-${indicator?.widgetKey}`
                        }
                    />
                )}
                {showCopyButton && (
                    <Button
                        className={`${pendoClassNamePrefix}-add-to-dashboard`}
                        onClick={() => setShowAddToDashboardModal(true)}
                        size="small"
                        variant={isExploreUser ? 'outlined' : 'contained'}
                        sx={{ flex: 1 }}
                    >
                        {isModel
                            ? t('indicators.addToDashboard')
                            : t('indicators.copyToDashboard')}
                    </Button>
                )}
                {isExploreUser &&
                    (onUseAsModel ? (
                        <Button
                            className={`${pendoClassNamePrefix}-use-model`}
                            component="a"
                            href={useModelUrl}
                            onClick={(e: MouseEvent) => {
                                e.preventDefault();
                                onUseAsModel();
                            }}
                            size="small"
                            variant="contained"
                            sx={{ flex: 1 }}
                        >
                            {isModel
                                ? t('indicators.useThisModelExplore')
                                : t('indicators.useAsModel')}
                        </Button>
                    ) : (
                        <Button
                            className={`${pendoClassNamePrefix}-use-model`}
                            component={Link}
                            to={useModelUrl}
                            size="small"
                            variant="contained"
                            sx={{ flex: 1 }}
                        >
                            {isModel
                                ? t('indicators.useThisModel')
                                : t('indicators.useAsModel')}
                        </Button>
                    ))}
            </Flex>
            {showAddToDashboardModal && (
                <AddToDashboardModal
                    show={showAddToDashboardModal}
                    closeModal={() => setShowAddToDashboardModal(false)}
                    query={modelQuery}
                    settings={{
                        selectedViz: vizType,
                        chartTitle: title,
                        chartSettings: settings,
                    }}
                />
            )}
        </>
    );
};

export default IndicatorActions;
